import React, { useCallback, useEffect, useState } from 'react';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import { useOutletContext } from 'react-router';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import usePageTitle from 'hooks/usePageTitle';
import { CONTENT_TOP } from 'dashboard/layouts/admin/chromeGeometry';
import {
  EmptyState, GlassTabs, LoadingOverlay, PagePaper,
} from 'dashboard/components/common';
import TicketThread from './TicketThread';

/**
 * Support tickets the brand has received.
 *
 * Listed longest-waiting first rather than newest first. "Who has been
 * waiting for an answer" is the only question this screen exists to settle,
 * and sorting by arrival buries exactly the person who has waited most.
 */

const TABS = ['Waiting', 'Answered', 'Closed', 'All'];
const STATUS_FOR_TAB = ['open', 'answered', 'closed', ''];

const STATUS_LABEL = { open: 'Waiting', answered: 'Answered', closed: 'Closed' };
const STATUS_COLOUR = { open: 'warning', answered: 'info', closed: 'default' };

/**
 * How long this person has been waiting, in words.
 *
 * Measured from their last message, not from when the ticket was opened: one
 * answered twice and asked about again this morning is this morning's
 * problem, not last week's.
 */
function waitedFor(ticket) {
  const since = ticket.status === 'open' ? ticket.last_customer_at : ticket.updated_at;
  if (!since) return '';
  const mins = Math.round((Date.now() - new Date(since).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'}`;
}

export default function Tickets() {
  usePageTitle('Support', 'Questions from your customers, and what you told them.');
  const [selectedBrand] = useOutletContext();
  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    if (!selectedBrand) { setLoading(false); return Promise.resolve(); }
    setLoading(true);
    return API.listBrandTickets({ brand_id: selectedBrand, status: STATUS_FOR_TAB[tab] })
      .then((res) => setRows(Array.isArray(res) ? res : []))
      .catch((err) => toast.error(err?.message || 'Could not load tickets'))
      .finally(() => setLoading(false));
  }, [selectedBrand, tab]);

  useEffect(() => { load(); }, [load]);

  if (!selectedBrand) {
    return (
      <Box sx={{ pt: CONTENT_TOP }}>
        <PagePaper><EmptyState>Select a brand to see its support tickets.</EmptyState></PagePaper>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: CONTENT_TOP }}>
      <PagePaper>
        <GlassTabs value={tab} onChange={(_e, v) => { setTab(v); setOpenId(null); }} tabs={TABS} />

        <Box mt='18px'>
          <LoadingOverlay loading={loading} minHeight={220}>
            {rows.length === 0 ? (
              <EmptyState>
                {tab === 0
                  ? 'Nobody is waiting on you. Anything your customers send will land here.'
                  : 'Nothing here yet.'}
              </EmptyState>
            ) : (
              <Stack divider={<Divider />}>
                {rows.map((t) => (
                  <Box key={t._id} py='12px'>
                    <Stack
                      direction='row'
                      alignItems='center'
                      justifyContent='space-between'
                      gap='12px'
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setOpenId(openId === t._id ? null : t._id)}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight='700' noWrap>{t.subject}</Typography>
                        <Typography variant='caption' color='text.secondary'>
                          {t.messages?.length || 0} message
                          {(t.messages?.length || 0) === 1 ? '' : 's'}
                          {' · '}
                          {waitedFor(t)}
                        </Typography>
                      </Box>
                      <Chip
                        size='small'
                        label={STATUS_LABEL[t.status] || t.status}
                        color={STATUS_COLOUR[t.status] || 'default'}
                      />
                    </Stack>

                    {openId === t._id && (
                      <TicketThread ticket={t} brandId={selectedBrand} onChanged={load} />
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </LoadingOverlay>
        </Box>
      </PagePaper>
    </Box>
  );
}
